import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Skeleton } from "@/components/ui/skeleton";

import { dashboardScheduleWeek } from "@/lib/dashboardScheduleWeek";

const SKELETON_DAY_COUNT = 4;
const SKELETON_GUTTER_ROWS = 6;
const WEEKDAY_COUNT = 7;

// Scattered placeholder event blocks for the expanded grid, keyed by column.
const SKELETON_EVENTS: { column: number; topPx: number; heightPx: number }[] = [
  { column: 0, topPx: 24, heightPx: 64 },
  { column: 2, topPx: 96, heightPx: 44 },
  { column: 3, topPx: 168, heightPx: 72 },
  { column: 5, topPx: 56, heightPx: 52 },
];

export const DashboardScheduleSkeleton = ({
  variant = "mini",
}: {
  variant?: "rail" | "mini" | "expanded";
}) => {
  const renderRail = () => (
    <Column className="h-full w-[44px] shrink-0 items-center gap-3 overflow-hidden rounded-xl bg-lever-surface-neutral pb-2 pt-2">
      <Skeleton className="h-4 w-4 rounded" />
      {Array.from({ length: SKELETON_DAY_COUNT }, (_, i) => (
        <Skeleton key={i} className="h-7 w-7 rounded-full" />
      ))}
    </Column>
  );

  const renderDayGroup = (index: number) => (
    <Column key={index} className="gap-2.5">
      <Row className="items-center gap-2">
        <Skeleton className="h-3 w-16 shrink-0" />
        <div className="h-px flex-1 border-t border-dashed border-lever-border-light" />
      </Row>
      <Skeleton className="h-14 w-full rounded-lg" />
    </Column>
  );

  const renderMini = () => (
    <Column className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-lever-border-light bg-lever-surface-default">
      <Row className="h-12 shrink-0 items-center justify-between border-b border-lever-border-light bg-lever-surface-default-on-elevation px-3">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-6 w-6 rounded-md" />
      </Row>
      <Column className="min-h-0 flex-1 gap-5 overflow-hidden px-3 pb-4 pt-3">
        {Array.from({ length: SKELETON_DAY_COUNT }, (_, i) =>
          renderDayGroup(i),
        )}
      </Column>
      <Row className="shrink-0 items-center justify-end px-3 py-2.5">
        <Skeleton className="h-8 w-40 rounded-full" />
      </Row>
    </Column>
  );

  const renderExpandedColumn = (column: number) => (
    <div key={column} className="relative border-l border-lever-border-light">
      {SKELETON_EVENTS.filter((event) => event.column === column).map(
        (event) => (
          <Skeleton
            key={event.topPx}
            className="absolute inset-x-1 rounded-lg"
            style={{ top: event.topPx, height: event.heightPx }}
          />
        ),
      )}
    </div>
  );

  const renderExpanded = () => (
    <Column className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-lever-border-light bg-lever-surface-default">
      <Row className="h-12 shrink-0 items-stretch border-b border-lever-border-light bg-lever-surface-default-on-elevation">
        <div
          className="shrink-0 border-r border-lever-border-light"
          style={{ width: dashboardScheduleWeek.TIME_GUTTER_WIDTH_PX }}
        />
        <div
          className="grid min-w-0 flex-1"
          style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}
        >
          {Array.from({ length: WEEKDAY_COUNT }, (_, i) => (
            <Center key={i} className="border-l border-lever-border-light">
              <Skeleton className="h-3 w-10" />
            </Center>
          ))}
        </div>
      </Row>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Column
          className="shrink-0 items-end gap-8 border-r border-lever-border-light pr-1.5 pt-3"
          style={{ width: dashboardScheduleWeek.TIME_GUTTER_WIDTH_PX }}
        >
          {Array.from({ length: SKELETON_GUTTER_ROWS }, (_, i) => (
            <Skeleton key={i} className="h-2 w-6" />
          ))}
        </Column>
        <div
          className="grid min-w-0 flex-1"
          style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}
        >
          {Array.from({ length: WEEKDAY_COUNT }, (_, i) =>
            renderExpandedColumn(i),
          )}
        </div>
      </div>
      <Row className="shrink-0 items-center justify-between border-t border-lever-border-light px-3 py-2.5">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-8 w-40 rounded-full" />
      </Row>
    </Column>
  );

  if (variant === "rail") return renderRail();
  return variant === "expanded" ? renderExpanded() : renderMini();
};
